import { useState, useEffect } from 'react';
import api from '@/api';
import { Plus, Loader2, ShieldCheck, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const STATUS_STYLE = {
  ok: { icon: CheckCircle2, color: 'text-[#57EB31]', bg: 'bg-[#57EB31]/10 border-[#57EB31]/30', label: 'OK' },
  due_soon: { icon: Clock, color: 'text-[#3B82F6]', bg: 'bg-[#3B82F6]/10 border-[#3B82F6]/30', label: 'DUE SOON' },
  overdue: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-900/20 border-red-800/50', label: 'OVERDUE' },
  expired: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-900/20 border-red-800/50', label: 'EXPIRED' },
  unknown: { icon: ShieldCheck, color: 'text-[#5C6A7A]', bg: 'bg-[#1E293B] border-[#2D3F5E]', label: '—' },
};

const PM_TYPES = ["Oil Change", "Tire Rotation", "Brake Inspection", "Air Filter", "Coolant Flush", "Transmission Service", "Annual PM", "Pre-Trip Inspection", "Other"];
const DOT_TYPES = ["Annual DOT Inspection", "Vehicle Registration", "Insurance Certificate", "IFTA License", "Operating Authority (MC)", "Hazmat Permit", "Other"];

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.unknown;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${s.bg} ${s.color}`}>
      <s.icon size={9} strokeWidth={2.5} /> {s.label}
    </span>
  );
}

export default function PmDot() {
  const [tab, setTab] = useState('pm');
  const [pmList, setPmList] = useState([]);
  const [dotList, setDotList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPmModal, setShowPmModal] = useState(false);
  const [showDotModal, setShowDotModal] = useState(false);
  const [pmForm, setPmForm] = useState({ customer_id: '', truck_id: '', service_type: 'Oil Change', interval_miles: '', interval_days: '', last_service_mileage: '', next_due_mileage: '', next_due_date: '', notes: '' });
  const [dotForm, setDotForm] = useState({ customer_id: '', truck_id: '', record_type: 'Annual DOT Inspection', expiry_date: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pmRes, dotRes, cRes] = await Promise.all([
        api.get('/compliance/pm'),
        api.get('/compliance/dot'),
        api.get('/customers', { params: { limit: 100 } }),
      ]);
      setPmList(pmRes.data);
      setDotList(dotRes.data);
      setCustomers(cRes.data.items.filter(c => ['APPROVED','CONTRACT_SIGNED'].includes(c.onboarding_state)));
    } catch (e) {
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const loadTrucks = async (cid) => {
    if (!cid) return setTrucks([]);
    const res = await api.get('/trucks', { params: { customer_id: cid } });
    setTrucks(res.data);
  };

  const handleCreatePm = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/compliance/pm', {
        ...pmForm,
        interval_miles: pmForm.interval_miles ? Number(pmForm.interval_miles) : undefined,
        interval_days: pmForm.interval_days ? Number(pmForm.interval_days) : undefined,
        last_service_mileage: pmForm.last_service_mileage ? Number(pmForm.last_service_mileage) : undefined,
        next_due_mileage: pmForm.next_due_mileage ? Number(pmForm.next_due_mileage) : undefined,
      });
      toast.success('PM schedule created!');
      setShowPmModal(false);
      setPmForm({ customer_id: '', truck_id: '', service_type: 'Oil Change', interval_miles: '', interval_days: '', last_service_mileage: '', next_due_mileage: '', next_due_date: '', notes: '' });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateDot = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/compliance/dot', dotForm);
      toast.success('DOT record created!');
      setShowDotModal(false);
      setDotForm({ customer_id: '', truck_id: '', record_type: 'Annual DOT Inspection', expiry_date: '', notes: '' });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const deletePm = async (id) => {
    await api.delete(`/compliance/pm/${id}`);
    toast.success('Removed');
    loadAll();
  };

  const deleteDot = async (id) => {
    await api.delete(`/compliance/dot/${id}`);
    toast.success('Removed');
    loadAll();
  };

  const pmStats = { ok: pmList.filter(p => p.mileage_status === 'ok').length, due_soon: pmList.filter(p => p.mileage_status === 'due_soon').length, overdue: pmList.filter(p => p.mileage_status === 'overdue').length };
  const dotStats = { ok: dotList.filter(d => d.status === 'ok').length, due_soon: dotList.filter(d => d.status === 'due_soon').length, expired: dotList.filter(d => d.status === 'expired').length };

  return (
    <div className="p-6 space-y-5 animate-fade-in" data-testid="pm-dot-page">
      <div className="flex items-center justify-between pb-4 border-b border-[#2D3F5E]/60">
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-1">// FLEET COMPLIANCE</p>
          <h1 className="text-white font-['Manrope'] font-extrabold text-2xl">PM / DOT Tracker</h1>
        </div>
        <button onClick={() => tab === 'pm' ? setShowPmModal(true) : setShowDotModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#2563EB] transition-colors"
          data-testid="add-compliance-btn">
          <Plus size={14} /> Add {tab === 'pm' ? 'PM Schedule' : 'DOT Record'}
        </button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3">
        {(tab === 'pm' ? [
          { label: 'OK', value: pmStats.ok, style: STATUS_STYLE.ok },
          { label: 'DUE SOON', value: pmStats.due_soon, style: STATUS_STYLE.due_soon },
          { label: 'OVERDUE', value: pmStats.overdue, style: STATUS_STYLE.overdue },
        ] : [
          { label: 'VALID', value: dotStats.ok, style: STATUS_STYLE.ok },
          { label: 'EXPIRING SOON', value: dotStats.due_soon, style: STATUS_STYLE.due_soon },
          { label: 'EXPIRED', value: dotStats.expired, style: STATUS_STYLE.expired },
        ]).map(({ label, value, style }) => (
          <div key={label} className={`card-industrial p-4 border ${style.bg}`}>
            <p className={`text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2 ${style.color}`}>{label}</p>
            <p className={`text-3xl font-['JetBrains_Mono'] font-bold ${style.color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#2D3F5E]">
        {['pm', 'dot'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-2.5 text-xs font-bold uppercase tracking-widest font-['JetBrains_Mono'] transition-colors border-b-2 -mb-px ${tab === t ? 'text-[#3B82F6] border-[#3B82F6]' : 'text-[#5C6A7A] border-transparent hover:text-[#B8C0CC]'}`}
            data-testid={`tab-${t}`}>
            {t === 'pm' ? 'Preventive Maintenance' : 'DOT Compliance'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="text-[#3B82F6] animate-spin" size={24} /></div>
      ) : tab === 'pm' ? (
        <div className="card-industrial overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#2D3F5E]">
                {['', 'UNIT', 'SERVICE', 'LAST SERVICE', 'NEXT DUE (MI)', 'CURRENT MI', 'STATUS', ''].map((h, i) => (
                  <th key={i} className={`text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] ${h === '' ? 'w-2' : ''}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pmList.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-16 text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">
                  <ShieldCheck size={28} className="mx-auto mb-3 text-[#2D3F5E]" /> No PM schedules. Click "Add PM Schedule".
                </td></tr>
              ) : pmList.map((p, i) => (
                <tr key={p.id} className={`border-b border-[#2D3F5E]/30 hover:bg-[#2A1C14] transition-colors ${p.mileage_status === 'emergency' ? 'strip-emergency' : p.mileage_status === 'overdue' ? 'strip-emergency' : p.mileage_status === 'due_soon' ? 'strip-medium' : 'strip-low'}`}
                  style={{ background: i % 2 !== 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                  <td className="w-2 p-0" />
                  <td className="px-4 py-3 font-['JetBrains_Mono'] text-xs text-[#3B82F6] font-bold">{p.truck_unit_number}</td>
                  <td className="px-4 py-3 text-sm text-white font-['IBM_Plex_Sans']">{p.service_type}</td>
                  <td className="px-4 py-3 text-xs text-[#B8C0CC] font-['JetBrains_Mono']">
                    {p.last_service_mileage ? `${p.last_service_mileage.toLocaleString()} mi` : p.last_service_date || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm font-['JetBrains_Mono'] text-[#E8EAED] font-bold">
                    {p.next_due_mileage ? `${p.next_due_mileage.toLocaleString()} mi` : '—'}
                    {p.miles_remaining !== undefined && <span className={`ml-2 text-xs ${p.mileage_status === 'overdue' ? 'text-red-400' : p.mileage_status === 'due_soon' ? 'text-[#3B82F6]' : 'text-[#5C6A7A]'}`}>
                      ({p.miles_remaining > 0 ? `${p.miles_remaining.toLocaleString()} left` : `${Math.abs(p.miles_remaining).toLocaleString()} over`})
                    </span>}
                  </td>
                  <td className="px-4 py-3 text-xs font-['JetBrains_Mono'] text-[#B8C0CC]">{p.current_mileage?.toLocaleString()}</td>
                  <td className="px-4 py-3"><StatusBadge status={p.mileage_status} /></td>
                  <td className="px-4 py-3">
                    <button onClick={() => deletePm(p.id)} className="text-[#2D3F5E] hover:text-red-400 text-xs font-['JetBrains_Mono'] transition-colors">DEL</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card-industrial overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#2D3F5E]">
                {['', 'UNIT', 'RECORD TYPE', 'EXPIRY DATE', 'DAYS LEFT', 'STATUS', ''].map((h, i) => (
                  <th key={i} className="text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dotList.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-16 text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">
                  <ShieldCheck size={28} className="mx-auto mb-3 text-[#2D3F5E]" /> No DOT records. Click "Add DOT Record".
                </td></tr>
              ) : dotList.map((d, i) => (
                <tr key={d.id} className={`border-b border-[#2D3F5E]/30 hover:bg-[#2A1C14] transition-colors ${d.status === 'expired' ? 'strip-emergency' : d.status === 'due_soon' ? 'strip-medium' : 'strip-low'}`}
                  style={{ background: i % 2 !== 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                  <td className="w-2 p-0" />
                  <td className="px-4 py-3 font-['JetBrains_Mono'] text-xs text-[#3B82F6] font-bold">{d.truck_unit_number}</td>
                  <td className="px-4 py-3 text-sm text-white font-['IBM_Plex_Sans']">{d.record_type}</td>
                  <td className="px-4 py-3 text-sm font-['JetBrains_Mono'] text-[#E8EAED]">{d.expiry_date ? new Date(d.expiry_date).toLocaleDateString() : '—'}</td>
                  <td className={`px-4 py-3 text-sm font-['JetBrains_Mono'] font-bold ${d.status === 'expired' ? 'text-red-400' : d.status === 'due_soon' ? 'text-[#3B82F6]' : 'text-[#57EB31]'}`}>
                    {d.days_remaining !== null ? (d.days_remaining > 0 ? `${d.days_remaining}d` : `${Math.abs(d.days_remaining)}d ago`) : '—'}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                  <td className="px-4 py-3">
                    <button onClick={() => deleteDot(d.id)} className="text-[#2D3F5E] hover:text-red-400 text-xs font-['JetBrains_Mono'] transition-colors">DEL</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* PM Modal */}
      <Dialog open={showPmModal} onOpenChange={setShowPmModal}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E] max-w-lg">
          <DialogHeader><DialogTitle className="text-white font-['Manrope']">New PM Schedule</DialogTitle></DialogHeader>
          <form onSubmit={handleCreatePm} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Customer *</label>
                <select value={pmForm.customer_id} onChange={e => { setPmForm(p => ({...p, customer_id: e.target.value, truck_id: ''})); loadTrucks(e.target.value); }} required
                  className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]">
                  <option value="">Select...</option>{customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Unit *</label>
                <select value={pmForm.truck_id} onChange={e => setPmForm(p => ({...p, truck_id: e.target.value}))} required
                  className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]">
                  <option value="">Select...</option>{trucks.map(t => <option key={t.id} value={t.id}>{t.unit_number}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Service Type</label>
                <select value={pmForm.service_type} onChange={e => setPmForm(p => ({...p, service_type: e.target.value}))}
                  className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]">
                  {PM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {[['Last Service Mileage', 'last_service_mileage'], ['Next Due Mileage', 'next_due_mileage'], ['Interval (Miles)', 'interval_miles'], ['Interval (Days)', 'interval_days']].map(([label, key]) => (
                <div key={key}>
                  <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">{label}</label>
                  <input type="number" value={pmForm[key]} onChange={e => setPmForm(p => ({...p, [key]: e.target.value}))}
                    className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]" />
                </div>
              ))}
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Next Due Date</label>
                <input type="date" value={pmForm.next_due_date} onChange={e => setPmForm(p => ({...p, next_due_date: e.target.value}))}
                  className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]" />
              </div>
            </div>
            <button type="submit" disabled={saving} className="w-full h-10 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#2563EB] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />} Add PM Schedule
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* DOT Modal */}
      <Dialog open={showDotModal} onOpenChange={setShowDotModal}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E]">
          <DialogHeader><DialogTitle className="text-white font-['Manrope']">New DOT Record</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateDot} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Customer *</label>
                <select value={dotForm.customer_id} onChange={e => { setDotForm(p => ({...p, customer_id: e.target.value, truck_id: ''})); loadTrucks(e.target.value); }} required
                  className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]">
                  <option value="">Select...</option>{customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Unit *</label>
                <select value={dotForm.truck_id} onChange={e => setDotForm(p => ({...p, truck_id: e.target.value}))} required
                  className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]">
                  <option value="">Select...</option>{trucks.map(t => <option key={t.id} value={t.id}>{t.unit_number}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Record Type *</label>
                <select value={dotForm.record_type} onChange={e => setDotForm(p => ({...p, record_type: e.target.value}))} required
                  className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]">
                  {DOT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Expiry Date *</label>
                <input type="date" value={dotForm.expiry_date} onChange={e => setDotForm(p => ({...p, expiry_date: e.target.value}))} required
                  className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]" />
              </div>
            </div>
            <button type="submit" disabled={saving} className="w-full h-10 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#2563EB] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />} Add DOT Record
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
